<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-px-4">
			<!-- Preview switcher -->
			<EditorPreviewSwitcher
				:showPreview="showPreview"
				:label="$t('templates.graphQlVariables')"
				@preview:toggle="showPreview = $event"
				@preview:open="renderPreview"
				@render:click="renderPreview"
				v-model="previewVariables"
				class="s-mt-4 s-h-10"
			/>
			<!-- Workspace and Editor -->
			<JFlex class="s-mb-4 s-border s-border-gray-300 s-bg-white s-rounded-md s-pt-2">
				<DrawingWorkspace
					class="s-w-3/4"
					:templateId="id"
					:fileKey="templateFileKey"
					:drawings="templateDrawings"
					:showPreview="showPreview"
					:previewResult="previewResult"
					:isLoading="isPreviewLoading"
					@drawing:selected="setSelectedDrawingId"
					@drawing:added="handleAddDrawing"
					@drawing:updated="handleUpdateDrawing"
					@drawing:removed="handleRemoveDrawing"
					@drawing:error="fetchTemplate"
					:isSaving="isSaving"
				/>
				<DrawingEditor
					class="s-w-1/4"
					:templateId="id"
					:drawings="templateDrawings"
					:selectedDrawingId="selectedDrawingId"
					@drawing:isSaving="isSaving = $event"
					@drawing:updated="handleUpdateDrawing"
					@errorMessage="onError"
				/>
			</JFlex>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template" />
			</EditorAccordion>

			<!-- Pdf Drawing File Key -->
			<EditorAccordion :headline="$t('templates.pdfFile')" class="s-mb-4">
				<JBox class="s-my-4 s-bg-white s-p-4 s-border s-border-gray-300 s-rounded">
					<JFlex class="s-justify-end s-items-center s-mb-2">
						<JButton variant="default-light-sm" @click="toggleFilePicker">{{ $t("templates.selectOrUploadFile") }}</JButton>
					</JFlex>
					<JInputText v-model="templateFileKey" class="s-mb-4" :disabled="true" />
				</JBox>
			</EditorAccordion>

			<!-- Template Graphql Queries -->
			<EditorAccordion :headline="$t('core.graphQlQuery')" class="s-mb-4">
				<CGraphQlQueryLibrary v-model="template.graphQlQuery" />
			</EditorAccordion>

			<!-- Delete template-->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end s-mb-4" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>

			<!-- File Picker-->
			<CModal variant="default-large" :isVisible="showFilePicker" @overlay:click="toggleFilePicker">
				<CFilesExplorer :isFilePicker="true" @file:selected="handleFileSelect" />
			</CModal>

			<!-- Preview Settings Modal -->
			<CModal :isVisible="isSettingsVisible">
				<JBox class="s-p-4">
					<JLabel class="s-font-semibold s-pb-1">{{ $t("templates.graphQlVariables") }}</JLabel>
					<CCodeEditor v-model="previewVariables" />
					<JFlex class="s-justify-between s-mt-4">
						<JButton @click="isSettingsVisible = false" variant="default-light">{{ $t("templates.done") }}</JButton>
					</JFlex>
				</JBox>
			</CModal>
		</JBox>

		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />
	</EditorShell>
</template>

<script>
import api from "@/api/api"
import { query, mutation } from "../api/queries"
import EditorShell from "../components/EditorShell"
import EditorAccordion from "../components/EditorAccordion"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"

import DrawingWorkspace from "../components/pdfDrawing/DrawingWorkspace/"
import DrawingEditor from "../components/pdfDrawing/DrawingEditor/"

export default {
	props: {
		id: {
			type: String,
			required: true
		}
	},
	components: {
		DrawingWorkspace,
		DrawingEditor,
		EditorShell,
		EditorAccordion,
		EditorPreviewSwitcher,
		EditorSaveChangesPrompt,
		EditorDocumentSettings,
		EditorDebugConsole
	},
	data() {
		return {
			isLoading: false,
			template: {},
			originalTemplate: {},
			templateFileKey: null,

			showPreview: false,
			isSettingsVisible: false,
			previewVariables: null,
			previewData: null,
			previewResult: null,
			isPreviewLoading: false,

			templateDrawings: [],
			selectedDrawingId: null,

			showFilePicker: false,

			errors: [],

			autoSaveTimer: 0,
			isSaving: false
		}
	},

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		}
	},

	methods: {
		onError(error) {
			this.setErrors()
			this.setErrors([error])
		},
		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: query.fetchPdfDrawingTemplate,
				variables: { where: { id: this.id } }
			})
			this.isLoading = false

			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) | [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}

			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {})
		},
		toggleFilePicker() {
			this.showFilePicker = !this.showFilePicker
		},
		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors()

			const res = await api({
				query: mutation.updatePdfDrawingTemplate,
				variables: {
					templateId: this.id,
					input: {
						name: this.template?.name,
						description: this.template?.description,
						logicalId: this.template?.logicalId,
						passwordLiquid: this.template?.passwordLiquid,
						fileKey: this.templateFileKey,
						graphQlQuery: this.template?.graphQlQuery
					}
				}
			})

			if (res?.data?.updatePdfDrawingTemplate?.status !== "success") {
				this.setErrors(res?.data?.updatePdfDrawingTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template)
		},
		setSelectedDrawingId(event) {
			this.selectedDrawingId = event
		},

		invalidateDataForPreview() {
			this.previewData = null
		},

		async getMemoizedDataForPreview() {
			const query = this?.template?.graphQlQuery ?? ""
			let variables = { limit: 1 }

			if (!query) return Promise.resolve({ isSuccess: true, errors: null, data: null })

			try {
				variables = { ...variables, ...JSON.parse(this.previewVariables) }
			} catch (e) {
				return Promise.resolve({ isSuccess: false, errors: [e], data: null })
			}

			const res = await api({ query, variables })

			if (res?.errors) {
				return Promise.resolve({ isSuccess: false, errors: res?.errors?.map(item => item.message), data: null })
			}

			this.previewData = res?.data
			return Promise.resolve({ isSuccess: true, data: res?.data, errors: null })
		},

		async renderPreview() {
			if (this.isPreviewLoading) return
			this.isPreviewLoading = true
			this.previewResult = ""
			this.setErrors()

			const { isSuccess, errors, data } = await this.getMemoizedDataForPreview()
			if (!isSuccess) {
				this.setErrors(errors, { hint: this.$t("templates.problemWithGql") })
				this.isPreviewLoading = false
				return
			}

			const res = await api({
				query: query.renderPdfDrawingTemplate,
				variables: {
					templateId: this.id,
					input: {
						name: "data",
						contentJsonString: data ? JSON.stringify(data) : "{}"
					}
				}
			})
			this.isPreviewLoading = false

			const errorMessage = res?.data?.renderPdfDrawingTemplate?.errors?.map(item => {
				return item?.split("|")?.[2]
			})

			if (res?.data?.renderPdfDrawingTemplate?.status !== "success") {
				this.setErrors(errorMessage)
				return
			}

			this.previewResult = new Uint8Array(res?.data?.renderPdfDrawingTemplate?.value)
		},
		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id }
			})
			this.isLoading = false

			if (res?.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		handleUpdateDrawing(payload) {
			let found = this.templateDrawings?.find(item => item.id === this.selectedDrawingId)
			found.font = payload?.font || found?.font
			found.liquidSource = payload?.liquidSource || found?.liquidSource
			found.page = payload?.page || found?.page
			found.point1 = payload?.point1 || found?.point1
			found.point2 = payload?.point2 || found?.point2
			found.textAlignment = payload?.textAlignment || found?.textAlignment
			found.verticalAlignment = payload?.verticalAlignment || found?.verticalAlignment
		},
		handleAddDrawing(payload) {
			this.templateDrawings.push(payload)
		},
		handleRemoveDrawing() {
			let clonedObj = JSON.parse(JSON.stringify(this.templateDrawings))
			this.templateDrawings = clonedObj?.filter(item => item.id !== this.selectedDrawingId)
		},
		handleFileSelect(fileObj) {
			this.toggleFilePicker()
			this.templateFileKey = fileObj.key

			this.updateTemplate()
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		}
	},

	created() {
		this.fetchTemplate()
	},
	watch: {
		template: {
			handler() {
				this.templateFileKey = this.template?.fileKey
				this.templateDrawings = this.template?.drawings
			},
			deep: true
		}
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, hasChanged: this.hasChanged })
	}
}
</script>
